// Admin Conversations — live, drillable view of every investor's chat
// with Kavya. Bankers aren't assigned yet, so PC needs to see who is
// asking what so he can preempt or hand off intelligently.
//
// Layout:
//   left  — list of investors who have ever chatted, sorted by most-recent
//           activity. Each row shows today's message count, lifetime
//           count, and the latest summary subject so PC can scan without
//           clicking in.
//   right — selected investor: list of session dates, the AI summary for
//           the active date (subject, summary, key topics, action items),
//           and the verbatim transcript below.
//
// Data source: /api/admin/conversations and /api/admin/conversations/:id.
// Both endpoints require the admin token in localStorage.

import { useEffect, useMemo, useState } from "react";
import {
  MessageCircle, RefreshCw, User, Calendar, Bot, AlertCircle,
  ChevronRight, Search,
} from "lucide-react";
// Types come from the OpenAPI contract (lib/api-spec/openapi.yaml) via orval
// codegen. Do not redeclare these inline — if the server response ever
// drifts, regen the spec and these imports will break the build, surfacing
// the contract change before runtime.
import type {
  AdminConversationsRollupRow as ConversationRow,
  AdminConversationDrillDownResponse as DrillResponse,
} from "@workspace/api-client-react";

const ADMIN = () => localStorage.getItem("admin_token") || "";

async function api<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", "x-admin-token": ADMIN(), ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const t = await res.text();
    let msg = t;
    try { msg = JSON.parse(t)?.error?.message || JSON.parse(t).error || t; } catch { /* leave as text */ }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return res.json();
}

function relativeAgo(iso: string | Date): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AdminConversations() {
  const [rows, setRows] = useState<ConversationRow[] | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillResponse | null>(null);
  const [drillErr, setDrillErr] = useState<string | null>(null);
  const [drillBusy, setDrillBusy] = useState(false);

  const loadList = async () => {
    setListErr(null);
    try {
      const r = await api<ConversationRow[]>(`/admin/conversations`);
      setRows(r);
      // Auto-pick the most recently active investor on first load so the
      // operator sees real content immediately, not an empty pane.
      if (r.length > 0 && selectedId === null) setSelectedId(r[0].investorId);
    } catch (e: any) {
      setListErr(e?.message || "Failed to load conversations");
    }
  };

  useEffect(() => { loadList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Reload the drill-down whenever the selected investor or date changes.
  // We pass ?date= when the user picks a non-default session.
  useEffect(() => {
    if (selectedId === null) return;
    let cancelled = false;
    setDrillBusy(true);
    setDrillErr(null);
    const path = selectedDate
      ? `/admin/conversations/${selectedId}?date=${encodeURIComponent(selectedDate)}`
      : `/admin/conversations/${selectedId}`;
    api<DrillResponse>(path)
      .then((d) => {
        if (cancelled) return;
        setDrill(d);
        // Track the actual transcript date the server returned, so the
        // session list highlights the right row even when no ?date was
        // requested.
        if (!selectedDate) setSelectedDate(d.transcriptDate);
      })
      .catch((e) => { if (!cancelled) setDrillErr(e?.message || "Failed to load conversation"); })
      .finally(() => { if (!cancelled) setDrillBusy(false); });
    return () => { cancelled = true; };
  }, [selectedId, selectedDate]);

  // Reset the date when the investor changes — otherwise we'd carry a
  // date that may not exist in the new investor's session list.
  const onSelectInvestor = (id: number) => {
    if (id === selectedId) return;
    setSelectedId(id);
    setSelectedDate(null);
    setDrill(null);
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      (r.firm || "").toLowerCase().includes(q) ||
      (r.latestSummary?.decoded?.subject || "").toLowerCase().includes(q) ||
      (r.latestSummary?.decoded?.keyTopics || []).some((t) => t.toLowerCase().includes(q)),
    );
  }, [rows, filter]);

  const selectedRow = rows?.find((r) => r.investorId === selectedId) || null;
  const activeDecoded = drill?.sessions.find((s) => s.sessionDate === drill.transcriptDate)?.summary?.decoded || null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#FF5C00]" />
            Conversations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live view of what every investor is asking Kavya. Until bankers are assigned, this is your radar.
          </p>
        </div>
        <button
          onClick={loadList}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md border border-border hover:bg-muted">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT: investor list */}
        <div className="lg:col-span-5 border border-border rounded-md overflow-hidden flex flex-col bg-background">
          <div className="px-3 py-2 border-b border-border flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name, email, firm, or topic"
              className="flex-1 text-xs bg-transparent outline-none" />
            {rows && <span className="text-[10px] text-muted-foreground">{filtered.length}/{rows.length}</span>}
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-border max-h-[calc(100vh-260px)]">
            {listErr && <div className="p-4 text-xs text-red-700 flex items-start gap-2"><AlertCircle className="w-3.5 h-3.5 mt-0.5" /> {listErr}</div>}
            {!listErr && rows === null && <div className="p-4 text-xs text-muted-foreground">Loading conversations...</div>}
            {rows !== null && filtered.length === 0 && (
              <div className="p-6 text-center text-xs text-muted-foreground">
                {rows.length === 0 ? "No investor has chatted with Kavya yet." : "No matches for that filter."}
              </div>
            )}
            {filtered.map((r) => {
              const active = r.investorId === selectedId;
              const subject = r.latestSummary?.decoded?.subject;
              const topics = r.latestSummary?.decoded?.keyTopics || [];
              return (
                <button
                  key={r.investorId}
                  onClick={() => onSelectInvestor(r.investorId)}
                  className={`w-full text-left px-3 py-3 hover:bg-muted/50 transition-colors ${active ? "bg-[#FF5C00]/10 border-l-2 border-[#FF5C00]" : "border-l-2 border-transparent"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold truncate">{r.name}</span>
                        {r.status !== "active" && (
                          <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{r.status}</span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {r.email}{r.firm ? ` · ${r.firm}` : ""}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      <span className="text-[10px] text-muted-foreground">{relativeAgo(r.lastMsgAt)}</span>
                      <div className="flex items-center gap-1">
                        {r.todayMsgs > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF5C00] text-white font-semibold">
                            {r.todayMsgs} today
                          </span>
                        )}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {r.totalMsgs} total
                        </span>
                      </div>
                    </div>
                  </div>
                  {subject && (
                    <p className="mt-2 text-[11px] text-foreground/80 line-clamp-1">
                      <span className="text-muted-foreground">Last: </span>{subject}
                    </p>
                  )}
                  {topics.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {topics.slice(0, 3).map((t, i) => (
                        <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF5C00]/10 text-[#FF5C00] border border-[#FF5C00]/20 truncate max-w-[160px]">
                          {t}
                        </span>
                      ))}
                      {topics.length > 3 && (
                        <span className="text-[9px] text-muted-foreground">+{topics.length - 3}</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* RIGHT: drill-down */}
        <div className="lg:col-span-7 space-y-4">
          {selectedRow === null && (
            <div className="border border-border rounded-md p-10 text-center text-sm text-muted-foreground">
              Pick an investor on the left to see their conversation with Kavya.
            </div>
          )}

          {selectedRow !== null && (
            <>
              <div className="border border-border rounded-md p-4 bg-background">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-[#FF5C00]" />
                      <h2 className="text-base font-semibold truncate">{selectedRow.name}</h2>
                      {selectedRow.status !== "active" && (
                        <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{selectedRow.status}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {selectedRow.email}
                      {selectedRow.firm ? ` · ${selectedRow.firm}` : ""}
                      {selectedRow.type ? ` · ${selectedRow.type}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Last active</div>
                    <div className="text-xs font-medium">{relativeAgo(selectedRow.lastMsgAt)}</div>
                  </div>
                </div>
              </div>

              {/* Session date list */}
              <div className="border border-border rounded-md bg-background">
                <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                  <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" /> Sessions
                  </div>
                  {drill && <span className="text-[10px] text-muted-foreground">{drill.sessions.length} day{drill.sessions.length === 1 ? "" : "s"}</span>}
                </div>
                <div className="max-h-32 overflow-y-auto">
                  {drillBusy && !drill && <div className="p-3 text-xs text-muted-foreground">Loading...</div>}
                  {drill && drill.sessions.length === 0 && <div className="p-3 text-xs text-muted-foreground">No sessions found.</div>}
                  {drill && drill.sessions.map((s) => {
                    const active = s.sessionDate === drill.transcriptDate;
                    return (
                      <button
                        key={s.sessionDate}
                        onClick={() => setSelectedDate(s.sessionDate)}
                        className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-muted/50 ${active ? "bg-[#FF5C00]/10 font-semibold" : ""}`}>
                        <span>{s.sessionDate}</span>
                        <span className="flex items-center gap-2 text-[10px] text-muted-foreground">
                          {s.summary ? <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-700 border border-green-500/30">summary</span> : <span className="px-1.5 py-0.5 rounded bg-muted">no summary</span>}
                          <span>{s.messageCount} msg{s.messageCount === 1 ? "" : "s"}</span>
                          <ChevronRight className="w-3 h-3" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {drillErr && (
                <div className="border border-red-500/30 bg-red-500/10 rounded-md p-3 text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5" /> {drillErr}
                </div>
              )}

              {/* Summary card for the active session */}
              {activeDecoded && (
                <div className="border border-border rounded-md p-4 bg-background space-y-3">
                  {activeDecoded.subject && (
                    <h3 className="text-sm font-semibold text-[#FF5C00]">{activeDecoded.subject}</h3>
                  )}
                  <p className="text-xs leading-relaxed text-foreground/90 whitespace-pre-wrap">{activeDecoded.summary}</p>
                  {activeDecoded.keyTopics.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Topics discussed</div>
                      <div className="flex flex-wrap gap-1.5">
                        {activeDecoded.keyTopics.map((t, i) => (
                          <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-[#FF5C00]/10 text-[#FF5C00] border border-[#FF5C00]/20">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {activeDecoded.actionItems.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Action items</div>
                      <ul className="list-disc list-inside text-xs space-y-1 text-foreground/90">
                        {activeDecoded.actionItems.map((a, i) => <li key={i}>{a}</li>)}
                      </ul>
                    </div>
                  )}
                  {activeDecoded.agentsConsulted.length > 0 && (
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Bot className="w-3 h-3" />
                      Agents consulted: {activeDecoded.agentsConsulted.join(", ")}
                    </div>
                  )}
                </div>
              )}

              {/* Verbatim transcript */}
              {drill && drill.messages.length > 0 && (
                <div className="border border-border rounded-md bg-background">
                  <div className="px-3 py-2 border-b border-border text-xs font-mono uppercase tracking-wider">
                    Transcript · {drill.transcriptDate} · {drill.messages.length} message{drill.messages.length === 1 ? "" : "s"}
                  </div>
                  <div className="p-3 space-y-3 max-h-[500px] overflow-y-auto">
                    {drill.messages.map((m) => {
                      const isInvestor = m.role === "user";
                      return (
                        <div key={m.id} className={`flex ${isInvestor ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[85%] rounded-md px-3 py-2 ${isInvestor ? "bg-[#FF5C00]/10 border border-[#FF5C00]/30" : "bg-muted border border-border"}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] font-semibold uppercase tracking-wide">
                                {isInvestor ? selectedRow.name.split(" ")[0] : (m.agentSource || "Kavya")}
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                            <p className="text-xs leading-relaxed whitespace-pre-wrap text-foreground/90">{m.content}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {drill && drill.messages.length === 0 && !drillBusy && (
                <div className="border border-border rounded-md p-6 text-center text-xs text-muted-foreground">
                  No messages on this date.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
